import { Grid, ListSubheader } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDateTimePicker,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../../shared/components';
import { useApi, useAuth } from '../../shared/context';
import { QueryKeys, useActiveDataRoomLendersQuery, useTimezonesQuery } from '../../shared/hooks';
import { CallStatuses, CallStatusesMap, DataRoomCall } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { getApiResponseData } from '../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../shared/utils/api';

interface CallFormProps {
	dataRoomGuid: string | undefined;
	call: DataRoomCall | null;
	close: () => void;
}

export const CallForm: React.FC<CallFormProps> = ({ dataRoomGuid, call, close }) => {
	const api = useApi();
	const { currentUser, isTeamMember } = useAuth();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const defaultTimezone = currentUser.defaultTimezone ?? 'Eastern Standard Time';

	const timezoneQuery = useTimezonesQuery();
	const lendersQuery = useActiveDataRoomLendersQuery(dataRoomGuid, {
		enabled: dataRoomGuid != null && currentUser != null && !currentUser.isBankUser,
		retry: false,
	});

	const addCallMutation = useMutation(
		async ({ values }: CallFormMutationVariables) => {
			const timezoneOffset = -(new Date().getTimezoneOffset() / 60);
			if (values.callDate != null && values.callDate.getTime !== undefined) {
				values.callDate.setTime(values.callDate.getTime() + timezoneOffset * 60 * 60 * 1000);
			}

			const patchValues = {
				bankDealProfileGuid: values.bankDealProfileGuid,
				callDate: values.callDate,
				callDetails: values.callDetails,
				callTimeZone: values.callTimeZone ?? '',
				callStatus: values.callStatus,
				sendEmail: values.sendEmail,
			};

			const { data, error } = await getApiResponseData<DataRoomCall>(
				!call || call.id == 0
					? api.post(CerebroApiEndpoints.Calls.Root(dataRoomGuid), patchValues)
					: api.patch(CerebroApiEndpoints.Calls.Existing(dataRoomGuid, call.guid), patchValues)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				if (currentUser.isBankUser || isTeamMember) {
					client.invalidateQueries(QueryKeys.DataRoomUsers.GetByUser(dataRoomGuid, currentUser.userGuid));
					client.invalidateQueries(QueryKeys.Calls.Lender(dataRoomGuid, call?.bankDealProfileGuid));
				} else {
					client.invalidateQueries(QueryKeys.Calls.Root(dataRoomGuid));
				}
				snackbar.enqueueSnackbar('Call with borrower was successfully proposed.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={call != null} onClose={close}>
			<CerebroHeading size="h1" gutterBottom>
				Schedule Call
			</CerebroHeading>

			<Formik
				initialValues={{
					bankDealProfileGuid: call?.bankDealProfileGuid,
					callDetails: call?.callDetails,
					callDate: call?.callDate ?? null,
					callTimeZone: call?.callTimeZone ?? defaultTimezone,
					callStatus: call?.callStatus ?? 'Proposed',
					sendEmail: currentUser.isBankUser || currentUser.isCompanyUser,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(addCallMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								{!currentUser.isBankUser && (
									<>
										<Grid item xs={11}>
											<CerebroLabel id="bankDealProfileGuid" required>
												Provider
											</CerebroLabel>
											<Field
												id="bankDealProfileGuid"
												as={CerebroDropdown}
												name="bankDealProfileGuid"
												disabled={isSubmitting}
												error={
													touched.bankDealProfileGuid &&
													errors.bankDealProfileGuid !== undefined
												}
											>
												{lendersQuery.data?.map(lender => (
													<CerebroDropdownMenuItem
														key={lender.bankDealProfileGuid}
														value={lender.bankDealProfileGuid}
													>
														{lender.fullName} ({lender.bankName})
													</CerebroDropdownMenuItem>
												))}
											</Field>
											{errors.bankDealProfileGuid && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.bankDealProfileGuid}
												</CerebroText>
											)}
										</Grid>

										<Grid item xs={11} md={6}>
											<CerebroLabel id="callDate">Call Date</CerebroLabel>
											<Field
												id="callDate"
												name="callDate"
												component={CerebroDateTimePicker}
												disabled={isSubmitting}
												error={touched.callDate && errors.callDate !== undefined}
											/>
											{errors.callDate && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.callDate}
												</CerebroText>
											)}
										</Grid>

										<Grid item xs={11}>
											<CerebroLabel id="callTimeZone" required>
												Time Zone
											</CerebroLabel>
											<Field
												id="callTimeZone"
												as={CerebroDropdown}
												name="callTimeZone"
												disabled={isSubmitting}
												error={touched.callTimeZone && errors.callTimeZone !== undefined}
											>
												<ListSubheader>US Time Zones</ListSubheader>
												{timezoneQuery.data
													?.filter(zone => zone.isUsBased)
													.map(zone => (
														<CerebroDropdownMenuItem key={zone.id} value={zone.id}>
															{zone.displayName}
														</CerebroDropdownMenuItem>
													))}
												<ListSubheader>Other Time Zones</ListSubheader>
												{timezoneQuery.data
													?.filter(zone => !zone.isUsBased)
													.map(zone => (
														<CerebroDropdownMenuItem key={zone.id} value={zone.id}>
															{zone.displayName}
														</CerebroDropdownMenuItem>
													))}
											</Field>
											{errors.callTimeZone && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.callTimeZone}
												</CerebroText>
											)}
										</Grid>
									</>
								)}

								<Grid item xs={11}>
									<CerebroLabel id="name">Call Details</CerebroLabel>
									<CerebroText variant="hint">
										Enter the best times and dates you would like to have a call with the borrower.
									</CerebroText>
									<CerebroText variant="hint">
										After submitting, your request will be shared with the Cerebro transaction
										manager who will coordinate scheduling with the borrower.
									</CerebroText>
									<Field
										id="callDetails"
										as={CerebroTextarea}
										name="callDetails"
										rows={10}
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('callDetails', errors, touched)}
									/>
									{FormikUtils.IsInvalid('callDetails', errors, touched) && (
										<CerebroText variant="error">{errors.callDetails}</CerebroText>
									)}
								</Grid>
								{!currentUser.isBankUser && (
									<Grid item xs={11}>
										<CerebroLabel id="callStatus">Status</CerebroLabel>
										<Field
											id="callStatus"
											as={CerebroDropdown}
											name="callStatus"
											disabled={isSubmitting}
										>
											{Object.entries(CallStatusesMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									</Grid>
								)}
								{currentUser.isEmployeeUser && (
									<>
										<Grid item xs={11}>
											<CerebroLabel id="sendEmail">Send Email</CerebroLabel>
											<Field
												id="sendEmail"
												as={CerebroYesNo}
												name="sendEmail"
												disabled={isSubmitting}
											/>
										</Grid>
									</>
								)}

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type CallFormMutationVariables = FormikUtils.MutateOnSubmitVariables<CallFormValues>;
interface CallFormValues {
	bankDealProfileGuid: string | undefined;
	callDate: Date | null;
	callTimeZone: string;
	callDetails: string | null | undefined;
	callStatus: CallStatuses;
	sendEmail: boolean;
}
type CallFormValueErrors = FormikErrors<CallFormValues>;
